import SurgeService from '../../services/SurgeService';
import { startFetch, startFetchExcel } from "../../services/fetch/startFetch";
import {
    getConceptosSurge,
    getMatriculasSurge,
    getPracticasYDispositivosSurge,
    getResolucionesSurge,
    insertConceptoSurge,
    insertPracticasYDispositivosSurge,
    removeConceptoSurge,
    removePracticasYDispositivosSurge,
    updateConceptoSurge,
    updatePracticasYDispositivosSurge,
    cleanEstadosSolicitudesSurge,
    getEstadosSolicitudesSurge,
    getSolicitudesSurge,
    getTrackingSolicitudSurge,
    cleanTrackingSolicitudSurge,
    getTipoArchivosSurge,
    getArchivosSolicitudesSurge,
    uploadArchivoSolicitudSurge,
    cleanArchivosSolicitudesSurge,
    getContadorSolicitudesSurge,
    updateSolicitudSurge,
    removeSolicitudSurge,
    getPaginacionSurge,
} from "../../reducers/recuperos/surgeActions";
import { useDispatch, useSelector } from "react-redux";
import { startAlerta } from "../../actions/ui/alertas";
import { handleErrorApi } from '../../helpers/randoms';
import { finishSpinner, startSpinner } from '../../actions/ui/spinner';

export const useSurge = () => {
    const dispatch = useDispatch();

    const clean = {
        estadosSolicitudes: () => { cleanEstadosSolicitudesSurge() },
        trackingSolicitud: () => { cleanTrackingSolicitudSurge() },
        archivos: () => { cleanArchivosSolicitudesSurge() },
    };

    const get = {
        all: {
            estadosSolicitudes: () => {
                dispatch(startFetch(
                    () => SurgeService.getEstadosSolicitudes(),
                    (data) => {
                        dispatch(getEstadosSolicitudesSurge(data));
                    })
                );
            },
            tipoArchivos: () => {
                dispatch(startFetch(
                    () => SurgeService.getTipoArchivos(),
                    (data) => {
                        dispatch(getTipoArchivosSurge(data));
                    })
                );
            }
        },
        byId: {
            trackingSolicitud: (id) => {
                dispatch(startFetch(
                    () => SurgeService.getTrackingSolicitud(id),
                    (values) => {
                        dispatch(getTrackingSolicitudSurge(values));
                    })
                );
            },
            solicitudes: (nroSolicitud, resetSeeker) => {
                // se usaba así en GestionSolicitudes:  surge.get.byId.solicitudes(solicitud, () => setSolicitud(""))
                dispatch(startFetch(
                    () => SurgeService.getSolicitudesById(nroSolicitud),
                    (values) => {
                        dispatch(getSolicitudesSurge(values));
                        resetSeeker();
                    })
                );
            },
        },
        byParameters: {
            archivos: ({ idTipoArchivo, ...parameters }) => {
                dispatch(startFetch(
                    () => SurgeService.getArchivosSolicitudes(idTipoArchivo, parameters),
                    (data) => {
                        dispatch(getArchivosSolicitudesSurge(data));
                    })
                );
            },
            solicitudes: (parameters) => {
                dispatch(startFetch(
                    () => SurgeService.searchSolicitudes(parameters),
                    (values) => {
                        dispatch(getSolicitudesSurge(values));
                    }),
                );
            },
            solicitudesConPaginado: async (parameters) => {
                try {
                    dispatch(startSpinner())

                    const solicitudesResponse = await SurgeService.searchSolicitudesConPaginado(parameters)
                    const bodySolicitudes = await solicitudesResponse.json();
                    if (solicitudesResponse.ok) {
                        dispatch(getSolicitudesSurge(bodySolicitudes.results));
                        dispatch(getPaginacionSurge(bodySolicitudes))
                    } else {
                        throw bodySolicitudes;
                    }

                    const contadorResponse = await SurgeService.searchContadorSolicitudes({
                        ...(parameters.fechaDesde ? { fechaDesde: parameters.fechaDesde } : {}),
                        ...(parameters.fechaHasta ? { fechaHasta: parameters.fechaHasta } : {}),
                        ...(parameters.idAfiliado ? { idAfiliado: parameters.idAfiliado } : {}),
                        ...(parameters.idSolicitud ? { idSolicitud: parameters.idSolicitud } : {})
                    })
                    const bodyContador = await contadorResponse.json();
                    if (contadorResponse.ok) {
                        dispatch(getContadorSolicitudesSurge(bodyContador));
                    } else {
                        throw bodyContador;
                    }

                    dispatch(finishSpinner());
                } catch (error) {
                    dispatch(finishSpinner());
                    handleErrorApi(
                        dispatch,
                        error,
                        "Error al obtener las Solicitudes",
                        "Ha ocurrido un error. Intente nuevamente"
                    );
                }
            },
            contadorSolicitudes: (parameters) => {
                dispatch(startFetch(
                    () => SurgeService.searchContadorSolicitudes(parameters),
                    (values) => {
                        dispatch(getContadorSolicitudesSurge(values));
                    }),
                );
            },
        }
    };

    const getAll = {
        conceptos: () => {
            dispatch(startFetch(
                () => SurgeService.getConceptos(),
                (values) => {
                    dispatch(getConceptosSurge(values));
                })
            );
        },
        resoluciones: () => {
            dispatch(startFetch(
                () => SurgeService.getResoluciones(),
                (values) => {
                    values.sort((a, b) => {
                        // Obtener la parte relevante de la descripción antes de cualquier " - "
                        const descA = a.descripcion.split(' - ')[0];
                        const descB = b.descripcion.split(' - ')[0];

                        // Extraer los años de la descripción
                        const yearA = parseInt(descA.split('/')[1], 10);
                        const yearB = parseInt(descB.split('/')[1], 10);

                        // Si los años son iguales, ordenar por el número antes de '/'
                        if (yearA === yearB) {
                            const numA = parseInt(descA.split('/')[0], 10);
                            const numB = parseInt(descB.split('/')[0], 10);
                            return numB - numA;
                        }
                        return yearB - yearA;
                    });
                    dispatch(getResolucionesSurge(values));
                })
            );
        },
        matriculas: () => {
            dispatch(startFetch(
                () => SurgeService.getMatriculas(),
                (values) => {
                    dispatch(getMatriculasSurge(values));
                })
            );
        },
        practicasYDispositivos: () => {
            dispatch(startFetch(
                () => SurgeService.getPracticasYDispositivos(),
                (values) => {
                    dispatch(getPracticasYDispositivosSurge(values));
                })
            );
        },
        solicitudes: (values) => {
            dispatch(startFetch(
                () => SurgeService.getSolicitudes(values),
                (values) => {
                    dispatch(getSolicitudesSurge(values));
                })
            );
        }
    }

    const insert = {
        concepto: (values) => {
            dispatch(startFetch(
                () => SurgeService.insertConcepto(values),
                (data) => {
                    dispatch(insertConceptoSurge({ ...values, id: data }));
                    dispatch(
                        startAlerta(
                            `Concepto Cargado`,
                            'success',
                            'Comando Exitoso',
                        ),
                    )
                })
            );
        },
        practicaYDispositivo: (values, handleClose) => {
            dispatch(startFetch(
                () => SurgeService.insertPracticasYDispositivos({
                    ...values,
                    conceptoSurge: values.conceptoSurge.id,
                    resolucionSurge: values.resolucionSurge.id
                }),
                (idPracticaDispositivo) => {
                    dispatch(insertPracticasYDispositivosSurge({
                        ...values, idPracticaDispositivo,
                        concepto: values.conceptoSurge.id,
                        resolucion: values.resolucionSurge.id
                    }));
                    handleClose();
                    dispatch(
                        startAlerta(
                            `Practica y Dispositivo Agregados`,
                            'success',
                            'Comando Exitoso',
                        ),
                    )
                })
            );
        },
        solicitud: (values, resetForm, setAfiliado) => {
            dispatch(startFetch(
                () => SurgeService.nuevaSolicitud(values),
                () => {
                    dispatch(
                        startAlerta(
                            `Solicitud Cargada`,
                            'success',
                            'Comando Exitoso',
                        ),
                    )
                    resetForm()
                    setAfiliado(null)
                })
            );
        },
    }

    const update = {
        concepto: (values) => {
            dispatch(startFetch(
                () => SurgeService.updateConcepto(values),
                () => {
                    dispatch(updateConceptoSurge(values));
                    dispatch(
                        startAlerta(
                            `Concepto Guardado`,
                            'success',
                            'Comando Exitoso',
                        ),
                    )
                })
            );
        },
        practicaYDispositivo: (idPracticaDispositivo, values, handleClose) => {
            dispatch(startFetch(
                () => SurgeService.updatePracticasYDispositivos(idPracticaDispositivo, {
                    ...values,
                    conceptoSurge: values.conceptoSurge.id,
                    resolucionSurge: values.resolucionSurge.id
                }),
                () => {
                    dispatch(updatePracticasYDispositivosSurge({
                        ...values, idPracticaDispositivo,
                        concepto: values.conceptoSurge.id,
                        resolucion: values.resolucionSurge.id
                    }));
                    handleClose();
                    dispatch(
                        startAlerta(
                            `Practica y Dispositivo Modificados`,
                            'success',
                            'Comando Exitoso',
                        ),
                    );
                })
            );
        },
        aprobarSolicitud: (values, refreshTable) => {
            dispatch(startFetch(
                () => SurgeService.aprobarSolicitud({ ...values, idEstado: null }),
                () => {
                    refreshTable()
                    dispatch(
                        startAlerta(
                            `Estado Modificado Correctamente`,
                            'success',
                            'Comando Exitoso',
                        ),
                    )
                })
            );
        },
        rechazarSolicitud: (values, refreshTable) => {
            dispatch(startFetch(
                () => SurgeService.rechazarSolicitud({ ...values, idEstado: null }),
                () => {
                    refreshTable()
                    dispatch(
                        startAlerta(
                            `Estado Modificado Correctamente`,
                            'success',
                            'Comando Exitoso',
                        ),
                    )
                })
            );
        },
        notificacionSolicitud: (values, refreshTable) => {
            dispatch(startFetch(
                () => SurgeService.notificacionSolicitud({ ...values, idEstado: null }),
                () => {
                    refreshTable()
                    dispatch(
                        startAlerta(
                            `Estado Modificado Correctamente`,
                            'success',
                            'Comando Exitoso',
                        ),
                    )
                })
            );
        },
        pagarSolicitud: (values, refreshTable) => {
            dispatch(startFetch(
                () => SurgeService.pagarSolicitud({ ...values, idEstado: null }),
                () => {
                    refreshTable()
                    dispatch(
                        startAlerta(
                            `Estado Modificado Correctamente`,
                            'success',
                            'Comando Exitoso',
                        ),
                    )
                })
            );
        },
        devolverUltimoEstadoSolicitud: ({ idSolicitud }, refreshTable) => {
            dispatch(startFetch(
                () => SurgeService.devolverUltimoEstadoSolicitud({ idSolicitud }),
                () => {
                    refreshTable()
                    dispatch(
                        startAlerta(
                            `Estado Modificado Correctamente`,
                            'success',
                            'Comando Exitoso',
                        ),
                    )
                })
            );
        },
        solicitud: (idSolicitud, values) => {
            dispatch(startFetch(
                () => SurgeService.updateSolicitud(idSolicitud, values),
                () => {
                    dispatch(updateSolicitudSurge({ id: idSolicitud, ...values }))
                    dispatch(
                        startAlerta(
                            `Solicitud Modificada`,
                            'success',
                            'Comando Exitoso',
                        ),
                    )
                })
            );
        }
    }

    const remove = {
        solicitud: (data) => {
            dispatch(startFetch(
                () => SurgeService.removeSolicitud({ id: data }),
                () => {
                    dispatch(removeSolicitudSurge(data))
                    dispatch(
                        startAlerta(
                            `Solicitud Eliminada`,
                            'success',
                            'Comando Exitoso',
                        ),
                    )
                })
            );
        },
        concepto: (data) => {
            dispatch(startFetch(
                () => SurgeService.removeConcepto({ id: data }),
                () => {
                    dispatch(removeConceptoSurge(data));
                    dispatch(
                        startAlerta(
                            `Concepto Eliminado`,
                            'success',
                            'Comando Exitoso',
                        ),
                    )
                })
            );
        },
        practicaYDispositivo: (id, handleClose) => {
            dispatch(startFetch(
                () => SurgeService.removePracticasYDispositivos(id),
                () => {
                    dispatch(removePracticasYDispositivosSurge(id));
                    handleClose();
                    dispatch(
                        startAlerta(
                            `Practica y Dispositivo Eliminados`,
                            'success',
                            'Comando Exitoso',
                        ),
                    )
                })
            );
        },
    }

    const upload = {
        archivoSolicitud: (file, idTipoArchivo) => {
            dispatch(startFetch(
                () => SurgeService.uploadArchivoSolicitud(file, idTipoArchivo, dispatch),
                (data) => {
                    dispatch(uploadArchivoSolicitudSurge(data));
                    dispatch(
                        startAlerta(
                            `Archivo Cargado Correctamente`,
                            'success',
                            'Comando Exitoso',
                        ),
                    )
                })
            );
        }
    }

    const files = {
        solicitudesSSS: {
            excel: (parameters) => {
                dispatch(startFetchExcel(
                    () => SurgeService.getExcelSolicitudesSSS(parameters),
                    "SURGE-SolicitudesSSS.xlsx"
                ));
            },
            plantillaExcel: (parameters) => {
                dispatch(startFetchExcel(
                    () => SurgeService.getPlantillaExcel(parameters),
                    "SURGE-PlantillaSolicitudes.xlsx"
                ));
            }
        },
    }

    return {
        clean,
        get,
        getAll,
        insert,
        update,
        remove,
        upload,
        files,
        data: {
            estados: useSelector((state) => state.surge).estados,
            patologias: useSelector((state) => state.surge).patologias,
            conceptos: useSelector((state) => state.surge).conceptos,
            resoluciones: useSelector((state) => state.surge.resoluciones),
            matriculas: useSelector((state) => state.surge.matriculas),
            practicasYDispositivos: useSelector((state) => state.surge.practicasYDispositivos),
            solicitudes: useSelector((state) => state.surge.solicitudes),
            paginacion: useSelector((state) => state.surge.paginacion),
            contadorSolicitudes: useSelector((state) => state.surge.contadorSolicitudes),
            trackingSolicitud: useSelector((state) => state.surge.trackingSolicitud),
            archivos: useSelector((state) => state.surge.archivos),
            tipoArchivos: useSelector((state) => state.surge.tipoArchivos),
            estadosImagenes: useSelector((state) => state.surge.estadosImagenes),
        }
    }
}
